<template>
    <div class="header-buttons">
        <div v-for="item in menuItems" :key="item.id" class="header-buttons__item-wrapper kasanowa-hover" @click="navigateTo(item.url)">
            <div>{{ item.title }}</div>
        </div>
        <div class="header-buttons__profile-wrapper">
            <div class="header-buttons__name-wrapper">
                <div>{{ $t('header.loggedAs') }}:</div>
                <div class="kasanowa-bold header-buttons__name">{{ userInfo.name }} {{ userInfo.surname }}</div>
            </div>
            <div class="header-buttons__user-icon-wrapper kasanowa-hover" @click="handleProfileModalClick">
                <KIcon icon="user" width="37" height="37" />
            </div>
            <v-slide-y-transition>
                <div v-show="isProfileModalClick" id="logout-box" class="header-buttons__profile-menu">
                    <div v-if="isPh" @click="navigateTo(`/sales-rep/details/${userInfo?.id}`)" class="kasanowa-hover">{{ $t('header.profile') }}</div>
                    <div @click="navigateTo('/')" class="kasanowa-hover">{{ $t('header.logout') }}</div>
                </div>
            </v-slide-y-transition>
        </div>
    </div>
</template>

<script setup lang="ts">
import { defineProps, ref, onMounted, onBeforeUnmount } from 'vue';
import { navigateTo } from '@/hooks/NavigateHooks';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user';
import { ROUTE_ADMIN_PREVIEW_QUERY } from '@/hooks/RouterHooks';
import type { IMenuItems } from '@/models/LayoutModel';
import KIcon from '@/components/atoms/KIcon.vue';

interface IOwnProps {
    isMobileMenu: boolean
}
interface IProps extends IOwnProps {
}

defineProps<IProps>();

const isProfileModalClick = ref<boolean>(false);
const handleProfileModalClickListener = (event: MouseEvent) => {
    const logoutBox = document.getElementById('logout-box');
    const userIconWrapper = document.querySelector('.header-buttons__user-icon-wrapper');

    if (logoutBox &&
        !logoutBox.contains(event.target as Node) &&
        userIconWrapper &&
        !userIconWrapper.contains(event.target as Node)) {
        isProfileModalClick.value = false;
    }
};
onMounted(() => {
   window.addEventListener('click', handleProfileModalClickListener);
   window.addEventListener('scroll', handleProfileModalClickListener);
});
onBeforeUnmount(() => {
    window.removeEventListener('click', handleProfileModalClickListener);
    window.removeEventListener('scroll', handleProfileModalClickListener);
});
const handleProfileModalClick = () => {
    isProfileModalClick.value = !isProfileModalClick.value;
};
const { isAdmin, isPh, isAdminWholesaler, isPhReg, isAdminColian, userInfo } = storeToRefs(useUserStore());
const menuItems = ref<IMenuItems[]>([]);
if (isAdmin.value || isAdminColian.value) {
    menuItems.value = [
        {
            id: 0,
            title: t('header.menu1'),
            url: '/dashboard',
        },
        {
            id: 1,
            title: t('header.menu2'),
            url: '/quiz',
        },
        {
            id: 2,
            title: t('header.menu4'),
            url: '/articles',
        },
        {
            id: 3,
            title: t('header.menu3'),
            url: '/challenges',
        },
        {
            id: 4,
            title: t('header.menu5'),
            url: '/withdraw',
        },
        {
            id: 5,
            title: t('header.menu6'),
            url: `/dashboard?${ROUTE_ADMIN_PREVIEW_QUERY}=1`,
        },
    ];
}
if (isPhReg.value) {
    menuItems.value = [
        {
            id: 0,
            title: t('header.menu1'),
            url: '/dashboard',
        },
        {
            id: 3,
            title: t('header.menu3'),
            url: '/challenges',
        },
    ];
}
if (isAdminWholesaler.value) {
    menuItems.value = [
        {
            id: 0,
            title: t('header.menu1'),
            url: `/warehouse/details/${userInfo.value.wholesaler_ids[0]}`,
        },
        {
            id: 3,
            title: t('header.menu3'),
            url: '/challenges',
        },
    ];
}
if (isPh.value) {
    menuItems.value = [
        {
            id: 0,
            title: t('header.menu1'),
            url: '/dashboard',
        },
        {
            id: 1,
            title: t('header.menu2'),
            url: '/quiz-ph',
        },
        {
            id: 2,
            title: t('header.menu4'),
            url: '/articles-ph',
        },
        {
            id: 3,
            title: t('header.menu5'),
            url: '/withdraw',
        },
    ];
}
</script>

<style scoped lang="scss">
.header-buttons {
    padding: 24px;
    z-index: 100;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-size: 16px;
    width: 100%;
    fill: $white;

    @include media-breakpoint-up(lg) {
        flex-direction: row;
        padding: 0;
    }

    &__item-wrapper {
        padding: 12px;
        width: 100%;
        font-weight: 700;
        color: $white;
        border-bottom: 1px solid transparent;
        text-align: center;
        border-radius: 5px;
        transition: 0.3s;

        @include media-breakpoint-up(lg) {
            border-radius: 0;
            padding: 22px 0;
            margin-right: 23px;
            width: max-content;

            &:first-child {
                margin-left: 12px + 220px;
            }

            &:last-child {
                margin-right: 0;
            }
        }

        @include media-breakpoint-up(xl) {
            margin-right: 50px;
        }

        @include media-breakpoint-up(xxl) {
            margin-right: 70px;
        }

        &:hover {
            color: $secondary-400;
            background-color: rgba($secondary-400, 0.15);

            @include media-breakpoint-up(lg) {
                background-color: transparent;
                border-bottom: 1px solid $secondary-400;
            }
        }
    }

    &__profile-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        gap: 10px;
        margin-top: 24px;
        color: $white;

        @include media-breakpoint-up(lg) {
            margin-top: 0;
            margin-left: auto;
        }
    }

    &__name-wrapper {
        display: flex;
        flex-direction: column;
        text-align: right;
    }

    &__name {
        color: $secondary-400;
    }

    &__user-icon-wrapper {
        height: 37px;
    }

    &__profile-menu {
        position: absolute;
        top: 60px;
        right: -12px;
        background: rgba($black, 0.15);
        backdrop-filter: blur(30px) saturate(150%);
        -webkit-backdrop-filter: blur(30px) saturate(150%);
        border-radius: 5px;
        padding: 10px 30px;
        font-weight: 700;
        display: flex;
        flex-direction: column;
        gap: 10px;
    }
}
</style>
